import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Leaf, Sprout, Truck, Sparkles, Package, CalendarCheck, CheckCircle2, Megaphone, X } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";

const ICONS = {
  mowing: Sprout,
  weed_whacking: Scissors,
  pruning: Leaf,
  moving: Truck,
  special_request: Sparkles,
  full_yard: Package,
};

const HERO = "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const [services, setServices] = useState([]);
  const [ann, setAnn] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/announcement").then((r) => {
      const a = r.data;
      if (a.active && (a.title || a.text)) {
        const key = `hm_ann_${a.title}_${a.text}`;
        if (localStorage.getItem(key) !== "1") setAnn({ ...a, key });
      }
    }).catch(() => {});
  }, []);

  const dismissAnn = () => {
    if (ann) localStorage.setItem(ann.key, "1");
    setAnn(null);
  };

  return (
    <div className="App min-h-screen bg-background">
      {ann && (
        <div data-testid="announcement-bar" className="bg-accent text-accent-foreground">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-2.5 flex items-center gap-3 text-sm">
            <Megaphone className="w-4 h-4 shrink-0" />
            <p className="flex-1 leading-snug">
              {ann.title && <span className="font-bold">{ann.title}</span>}
              {ann.title && ann.text ? " — " : ""}
              {ann.text}
            </p>
            <button data-testid="announcement-dismiss" onClick={dismissAnn} className="shrink-0 opacity-80 hover:opacity-100 transition-opacity">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Freshly mowed lawn" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[hsl(142,45%,12%)]/70" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid md:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-9"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-white/80 mb-5">
              Local yardwork &amp; mowing
            </span>
            <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Help! Mowing and<br />Yardwork Assistance
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg max-w-xl leading-relaxed">
              Book a time that works for you. I show up, do the work, and you pay
              after it's done. No deposits, no hassle.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="hero-book-button"
                className="inline-flex items-center gap-2 bg-white text-primary font-bold px-7 py-3.5 rounded transition-transform active:scale-95 hover:bg-white/90"
              >
                Book a service <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-white/75 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Pay after the job is done
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28" id="services">
        <div className="max-w-2xl mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">What I offer</span>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">
            Simple services, honest prices
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = ICONS[s.id] || Sprout;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`service-card-${s.id}`}
                className="group bg-card border border-border rounded p-7 flex flex-col transition-transform hover:-translate-y-1 hover:shadow-sm"
              >
                <span className="grid place-items-center w-11 h-11 rounded bg-secondary text-primary mb-5">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="font-display font-semibold text-xl">{s.name}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed flex-1">
                  {s.description}
                </p>
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  {s.price != null ? (
                    <>
                      {s.on_sale && <span className="font-display text-lg text-muted-foreground line-through">${s.price}</span>}
                      <span className="font-display font-bold text-2xl text-primary">${s.on_sale ? s.sale_price : s.price}</span>
                      <span className="text-muted-foreground text-sm">/ {s.unit}</span>
                      {s.on_sale && <span className="text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded">{s.sale_label}</span>}
                    </>
                  ) : (
                    <span className="font-display font-bold text-xl text-primary">Custom quote</span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-primary text-primary-foreground grain">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 relative">
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mb-12">
            How booking works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Sparkles, t: "Pick a service", d: "Choose from mowing, weed whacking, pruning, moving help, or a special request." },
              { icon: CalendarCheck, t: "Choose a time", d: "Select from the dates and times I've marked as available." },
              { icon: CheckCircle2, t: "Pay afterwards", d: "I complete the work first. You pay once you're happy with the result." },
            ].map((step, i) => (
              <div key={i} data-testid={`how-step-${i}`} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-display font-black text-4xl text-white/30">{i + 1}</span>
                  <step.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-xl">{step.t}</h3>
                <p className="text-primary-foreground/80 text-sm leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/book"
              data-testid="how-book-button"
              className="inline-flex items-center gap-2 bg-white text-primary font-bold px-7 py-3.5 rounded transition-transform active:scale-95 hover:bg-white/90"
            >
              Start booking <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
        <span>© {new Date().getFullYear()} Help! Mowing and Yardwork Assistance</span>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-primary transition-colors">
          Owner login
        </Link>
      </footer>
    </div>
  );
}
